import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ArrowRight, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const BulkLoanReassignmentPage = () => {
  const [officers, setOfficers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromOfficer, setFromOfficer] = useState('');
  const [toOfficer, setToOfficer] = useState('');
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [offRes, loanRes] = await Promise.all([api.get('/loans/officers'), api.get('/loans?limit=200')]);
      setOfficers(offRes.data);
      setLoans(loanRes.data.loans || []);
    } catch { setOfficers([]); }
    setLoading(false);
  };

  const handleReassign = async () => {
    if (!fromOfficer || !toOfficer) { toast.error('Select both officers'); return; }
    if (fromOfficer === toOfficer) { toast.error('Source and target must be different'); return; }
    setReassigning(true);
    try {
      const res = await api.post('/loans/bulk-reassign', { from_officer_id: fromOfficer, to_officer_id: toOfficer });
      toast.success(res.data.message);
      load();
    } catch { toast.error('Reassignment failed'); }
    setReassigning(false);
  };

  const fromName = officers.find(o => o.id === fromOfficer)?.name || '';
  const toName = officers.find(o => o.id === toOfficer)?.name || '';

  return (
    <AdminSubPage title="Bulk Loan Reassignment" breadcrumb={['Admin', 'Organization', 'Bulk Loan Reassignment']} backPath="/dashboard/admin/organization">
      <div className="space-y-6">
        {/* Reassign Panel */}
        <div className="bg-white border border-gray-200 p-6" data-testid="bulk-reassign-panel">
          <h3 className="font-semibold text-[#1f1f1f] mb-4">Transfer Loans Between Officers</h3>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label>From Officer</Label>
              <Select value={fromOfficer} onValueChange={setFromOfficer}>
                <SelectTrigger data-testid="from-officer-select"><SelectValue placeholder="Select source officer" /></SelectTrigger>
                <SelectContent>{officers.map(o => <SelectItem key={o.id} value={o.id}>{o.name} ({o.loan_count} loans)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ArrowRight className="w-6 h-6 text-gray-400 flex-shrink-0 mb-2" />
            <div className="flex-1">
              <Label>To Officer</Label>
              <Select value={toOfficer} onValueChange={setToOfficer}>
                <SelectTrigger data-testid="to-officer-select"><SelectValue placeholder="Select target officer" /></SelectTrigger>
                <SelectContent>{officers.filter(o => o.id !== fromOfficer).map(o => <SelectItem key={o.id} value={o.id}>{o.name} ({o.loan_count} loans)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleReassign} disabled={reassigning || !fromOfficer || !toOfficer} className="bg-[#1f1f1f] text-white" data-testid="reassign-btn">
              {reassigning ? 'Reassigning...' : 'Reassign All'}
            </Button>
          </div>
          {fromOfficer && toOfficer && (
            <p className="text-sm text-gray-500 mt-3">All loans currently assigned to <strong>{fromName}</strong> will be transferred to <strong>{toName}</strong>.</p>
          )}
        </div>

        {/* Officers Overview */}
        <div className="bg-white border border-gray-200" data-testid="officers-table">
          <div className="px-4 py-3 border-b bg-gray-50"><h3 className="font-semibold text-sm text-[#1f1f1f]">Loan Officers Overview</h3></div>
          <table className="w-full">
            <thead><tr className="border-b">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Officer</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Loans Assigned</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} className="text-center py-8 text-gray-400">Loading...</td></tr> :
              officers.length === 0 ? <tr><td colSpan={4} className="text-center py-8 text-gray-400"><Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />No loan officers found.</td></tr> :
              officers.map(o => (
                <tr key={o.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{o.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{o.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{o.role}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono font-semibold">{o.loan_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminSubPage>
  );
};
export default BulkLoanReassignmentPage;
